import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AudioService {
  private mediaStream!: MediaStream;
  // @ts-ignore
  private mediaRecorder!: MediaRecorder;
  private chunks: Blob[] = [];
  private audioContext = new AudioContext();

  constructor() { }

  startRecording(): Promise<void> {
    return new Promise((resolve, reject) => {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then((stream) => {
          this.mediaStream = stream;
          // @ts-ignore
          this.mediaRecorder = new MediaRecorder(stream);

          this.mediaRecorder.ondataavailable = (e) => {
            this.chunks.push(e.data);
          };

          this.mediaRecorder.start();
          resolve();
        })
        .catch((err) => reject(err));
    });
  }

  stopRecording(): Promise<{ file: File; url: string }> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject('MediaRecorder is not initialized');
        return;
      }

      this.mediaRecorder.onstop = async () => {
        try {
          const audioBlob = new Blob(this.chunks, { type: 'audio/webm' });
          // @ts-ignore
          const arrayBuffer = await audioBlob.arrayBuffer();
          const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

          // Extract PCM data
          const pcmData = this.convertToPCM(audioBuffer);

          // Create a WAV file with PCM encoding
          const wavBlob = new Blob([this.createWavHeader(audioBuffer), pcmData], { type: 'audio/wav' });
          const file = new File([wavBlob], 'recording.wav', { type: 'audio/wav' });
          const audioUrl = URL.createObjectURL(file);

          this.chunks = [];
          this.releaseMicrophone();

          resolve({ file, url: audioUrl });
        } catch (error) {
          reject(`Error processing WAV PCM recording: ${error}`);
        }
      };

      this.mediaRecorder.stop();
    });
  }

  private convertToPCM(audioBuffer: AudioBuffer): Uint8Array {
    const raw = audioBuffer.getChannelData(0); // Get data for the first channel
    const pcm = new Int16Array(raw.length);

    for (let i = 0; i < raw.length; i++) {
      pcm[i] = raw[i] * 0x7FFF; // Convert float range (-1 to 1) to PCM range (-32768 to 32767)
    }

    return new Uint8Array(pcm.buffer); // Return the PCM data as a Uint8Array
  }

  private createWavHeader(audioBuffer: AudioBuffer): ArrayBuffer {
    const numChannels = 1; // Mono audio
    const sampleRate = audioBuffer.sampleRate;
    const bitsPerSample = 16; // 16-bit PCM
    const byteRate = sampleRate * numChannels * bitsPerSample / 8;
    const blockAlign = numChannels * bitsPerSample / 8;
    const dataSize = audioBuffer.length * numChannels * bitsPerSample / 8; // Size of audio data
    const chunkSize = 36 + dataSize; // Total file size minus the first 8 bytes for the "RIFF" chunk descriptor
    const riffHeader = new ArrayBuffer(44); // WAV file header is 44 bytes
    const headerView = new DataView(riffHeader);

    // RIFF Header
    this.writeString(headerView, 0, 'RIFF');
    headerView.setUint32(4, chunkSize, true);
    this.writeString(headerView, 8, 'WAVE');

    // fmt Chunk
    this.writeString(headerView, 12, 'fmt ');
    headerView.setUint32(16, 16, true); // Subchunk size (16 for PCM)
    headerView.setUint16(20, 1, true);  // Audio format (1 for PCM)
    headerView.setUint16(22, numChannels, true); // Number of channels
    headerView.setUint32(24, sampleRate, true); // Sample rate
    headerView.setUint32(28, byteRate, true); // Byte rate
    headerView.setUint16(32, blockAlign, true); // Block align
    headerView.setUint16(34, bitsPerSample, true); // Bits per sample

    // data Chunk
    this.writeString(headerView, 36, 'data');
    headerView.setUint32(40, dataSize, true);

    return riffHeader;
  }

  private writeString(view: DataView, offset: number, str: string): void {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  }

  private releaseMicrophone(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null!;
    }
  }
}
